// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React from 'react';

const section = { marginBottom: '1.5rem' };
const h2Style = { fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1F2937' };
const pStyle = { fontSize: '0.95rem', lineHeight: 1.7, color: '#374151', marginBottom: '0.5rem' };
const listStyle = { paddingLeft: '1.5rem', marginBottom: '0.75rem' };
const liStyle = { fontSize: '0.95rem', lineHeight: 1.7, color: '#374151', marginBottom: '0.25rem' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '1rem', fontSize: '0.9rem' };
const thStyle = { textAlign: 'left', padding: '10px 12px', background: '#F3F4F6', borderBottom: '2px solid #D1D5DB', fontWeight: 700, color: '#1F2937' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #E5E7EB', color: '#374151' };

const PrivacyPage = () => (
  <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto', fontFamily: 'system-ui' }}>
    <h1 style={{ fontSize: '1.75rem', fontWeight: 800, color: '#111827' }}>Privacy Policy</h1>
    <p style={{ fontWeight: 600, color: '#374151' }}>Miskulin Technologies Inc.</p>
    <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '2rem' }}>Last updated: February 20, 2026 | Version 2.0</p>

    <div style={section}>
      <h2 style={h2Style}>1. Information We Collect</h2>
      <p style={pStyle}>We collect information you provide when creating an account, including your name, email address, company information, and role. We also collect:</p>
      <ul style={listStyle}>
        <li style={liStyle}>Usage data (pages visited, features used, session duration)</li>
        <li style={liStyle}>Operational data you enter (jobs, customers, quotes, invoices, notes)</li>
        <li style={liStyle}>Device and browser information for security and optimization</li>
        <li style={liStyle}>IP addresses for security monitoring</li>
      </ul>
    </div>

    <div style={section}>
      <h2 style={h2Style}>2. Data Handling by Service Tier</h2>
      <p style={pStyle}>How we handle your data depends on your subscription tier:</p>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Tier</th>
            <th style={thStyle}>Data Ownership</th>
            <th style={thStyle}>MT Can Use Data</th>
            <th style={thStyle}>Retention</th>
            <th style={thStyle}>Deletion Timeline</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 600 }}>Demo Trial</td>
            <td style={tdStyle}>You</td>
            <td style={tdStyle}>No</td>
            <td style={tdStyle}>7 days</td>
            <td style={tdStyle}>48 hours after expiry</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 600 }}>Free</td>
            <td style={tdStyle}>MT (licensed)</td>
            <td style={{ ...tdStyle, fontWeight: 600, color: '#B45309' }}>Yes</td>
            <td style={tdStyle}>Indefinite</td>
            <td style={tdStyle}>30 days on request</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 600 }}>Growth</td>
            <td style={tdStyle}>You</td>
            <td style={tdStyle}>No</td>
            <td style={tdStyle}>Indefinite</td>
            <td style={tdStyle}>7 days on request</td>
          </tr>
          <tr>
            <td style={{ ...tdStyle, fontWeight: 600 }}>Scale</td>
            <td style={tdStyle}>You</td>
            <td style={tdStyle}>No</td>
            <td style={tdStyle}>Indefinite</td>
            <td style={tdStyle}>24 hours on request</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div style={section}>
      <h2 style={h2Style}>3. Demo Trial Data Scrubbing</h2>
      <p style={pStyle}>When a demo trial expires without conversion to a paid or free subscription:</p>
      <ul style={listStyle}>
        <li style={liStyle}>All data associated with the demo company is permanently deleted (hard delete).</li>
        <li style={liStyle}>Deletion covers all tables: business profiles, customer records, job records, financial records, communications, operational data, and analytics.</li>
        <li style={liStyle}>A <strong>Proof of Deletion Receipt</strong> is generated and made available to you, containing: unique receipt ID, timestamp, tables scrubbed, record counts deleted, and a formal confirmation statement.</li>
        <li style={liStyle}>No copies, backups, snapshots, or derived data are retained after scrubbing.</li>
        <li style={liStyle}>The deletion receipt itself is retained for our compliance records.</li>
      </ul>
    </div>

    <div style={section}>
      <h2 style={h2Style}>4. Free Tier Data Usage</h2>
      <p style={pStyle}>By using the free tier, you acknowledge and agree that Miskulin Technologies may use your data as follows:</p>
      <ul style={listStyle}>
        <li style={liStyle}><strong>Platform Improvement:</strong> Analyzing usage patterns to improve features and user experience.</li>
        <li style={liStyle}><strong>Anonymized Analytics:</strong> Generating aggregate industry insights (your identity is never disclosed).</li>
        <li style={liStyle}><strong>AI Training:</strong> Using anonymized data to train and improve AI models within the MT Platform.</li>
        <li style={liStyle}><strong>Product Development:</strong> Informing new feature development based on real-world usage.</li>
      </ul>
      <p style={pStyle}>We will NOT: sell your data to third parties, share your data with competitors, or use your data in ways that identify you or your business to others.</p>
      <p style={pStyle}>You may upgrade to a paid tier at any time to remove these data usage rights and gain full ownership of your data.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>5. Paid Tier Privacy Protections</h2>
      <p style={pStyle}>Growth and Scale tier subscribers receive full data privacy:</p>
      <ul style={listStyle}>
        <li style={liStyle}>Your data is 100% owned by you.</li>
        <li style={liStyle}>MT will not access your data except for technical support (with your explicit consent) or system maintenance/security.</li>
        <li style={liStyle}>Data is encrypted at rest (AES-256) and in transit (TLS 1.3).</li>
        <li style={liStyle}>Row Level Security (RLS) ensures no cross-company data access at the database level.</li>
        <li style={liStyle}>You may export all your data at any time in standard formats.</li>
      </ul>
    </div>

    <div style={section}>
      <h2 style={h2Style}>6. Data Security</h2>
      <p style={pStyle}>We implement industry-standard security measures including:</p>
      <ul style={listStyle}>
        <li style={liStyle}>Encryption at rest and in transit</li>
        <li style={liStyle}>Row Level Security (RLS) at the database level</li>
        <li style={liStyle}>No cross-company data access</li>
        <li style={liStyle}>Regular security audits</li>
        <li style={liStyle}>Anti-AI copy protection on all applications</li>
        <li style={liStyle}>Admin access is logged and auditable</li>
      </ul>
    </div>

    <div style={section}>
      <h2 style={h2Style}>7. Your Rights</h2>
      <ul style={listStyle}>
        <li style={liStyle}><strong>Access:</strong> Request a copy of all data we hold about you.</li>
        <li style={liStyle}><strong>Correction:</strong> Request correction of inaccurate data.</li>
        <li style={liStyle}><strong>Deletion:</strong> Request deletion of your data (timeline per your tier, see Section 2).</li>
        <li style={liStyle}><strong>Portability:</strong> Export your data in standard formats (paid tiers).</li>
        <li style={liStyle}><strong>Objection:</strong> Object to data processing (free tier users may upgrade to opt out).</li>
      </ul>
    </div>

    <div style={section}>
      <h2 style={h2Style}>8. Cookies & Tracking</h2>
      <p style={pStyle}>We use essential cookies for authentication and session management. We use analytics tracking to improve our services. No third-party advertising cookies are used.</p>
    </div>

    <div style={section}>
      <h2 style={h2Style}>9. Compliance</h2>
      <p style={pStyle}>This policy is designed to comply with the Personal Information Protection and Electronic Documents Act (PIPEDA) of Canada. We are working toward GDPR alignment and SOC 2 Type II certification.</p>
    </div>

    <div style={{ ...section, borderTop: '1px solid #E5E7EB', paddingTop: '1rem' }}>
      <h2 style={h2Style}>10. Contact</h2>
      <p style={pStyle}>Privacy inquiries or deletion requests: <a href="mailto:investor@miskulintechnologies.com" style={{ color: '#2563EB' }}>investor@miskulintechnologies.com</a></p>
      <p style={pStyle}>Miskulin Technologies Inc.<br />121 Rainbow Falls Bay, Chestermere, AB T1X 0S5, Canada</p>
    </div>
  </div>
);

export default PrivacyPage;
